import React, { useState } from "react";
import { Row, Col, Spinner, Button, Accordion, Alert } from "react-bootstrap";
import styled from "styled-components";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SaveIcon from "@mui/icons-material/Save";
import SuccessButton from "../../organisms/SuccessButton";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import PetsIcon from "@mui/icons-material/Pets";
import { useMutation } from "@tanstack/react-query";
import {
  buildModel,
  buildNuevoCarritoRequest,
  buildNuevoCarritoVacioRequest,
  CarritoHandlers,
  EliminarItemCarritoPayload,
  eliminarItemHandler,
  ItemCarritoHandlerPayload,
  recalcularModelConTotal,
  reemplazarPrecioFinalDiaIndividualGuarderia,
  reemplazarPrecioFinalNocheIndividualHotel,
  reemplazarPrecioFinalPlanGuarderia,
  reemplazarPrecioFinalTarifaHotel,
  reemplazarPrecioFinalTarifaSpa,
  reemplazarPrecioFinalTransporteIdaGuarderia,
  reemplazarPrecioFinalTransporteIdaHotel,
  reemplazarPrecioFinalTransporteIdaVueltaGuarderia,
  reemplazarPrecioFinalTransporteIdaVueltaHotel,
  reemplazarPrecioFinalTransporteVueltaGuarderia,
  reemplazarPrecioFinalTransporteVueltaHotel,
} from "./Model";
import ExitoCard, { ExitoOption } from "../../organisms/ExitoCard";
import {
  Carrito,
  ItemCarrito,
  reemplazarCarrito,
} from "../../../services/carritoServicios";
import { InfoRow } from "../../organisms/InfoRow";
import { SeccionMascota } from "./SeccionMascota";
import { ContentWrapperCard } from "./organisms/ContentWrappingCard";
import { IconContainer } from "./organisms/IconContainer";
import CurrencyFormat from "react-currency-format";
import { parseCurrency } from "../../../utils/numberUtils";
import {
  AgendamientoCarritoResponse,
  agendarDeCarrito,
} from "../../../services/agendamientosServicios";

function Resumen({
  carrito,
  onReemplazoCarritoSuccess,
}: {
  carrito: Carrito;
  onReemplazoCarritoSuccess: () => void;
}) {
  const carritoEstaVacio = carrito.items.length === 0;
  const [model, setNewModel] = useState(buildModel(carrito));
  const [termino, setTermino] = useState(false);
  const [idVentaResultante, setIdVentaResultante] = useState<
    string | undefined
  >(undefined);

  const {
    error: errorAgendar,
    isLoading: agendarIsLoading,
    mutate: mutarAgendar,
  } = useMutation(agendarDeCarrito, {
    onSuccess: (response: AgendamientoCarritoResponse) => {
      setIdVentaResultante(response.idVenta);
      setTermino(true);
    },
  });

  const {
    error: reemplazarCarritoIsError,
    isLoading: reemplazarCarritoIsLoading,
    mutate: mutarReemplazarCarrito,
  } = useMutation(reemplazarCarrito, {
    onSuccess: () => {
      onReemplazoCarritoSuccess();
      setNewModel((prevModel) => ({ ...prevModel, estaSucio: false }));
    },
  });

  const isError = errorAgendar || reemplazarCarritoIsError;
  const isLoading = agendarIsLoading || reemplazarCarritoIsLoading;

  const handlers: CarritoHandlers = {
    guarderia: {
      onPrecioFinalPlanChange: (payload: ItemCarritoHandlerPayload) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalPlanGuarderia(prevModel, payload);
        });
      },
      onPrecioFinalDiaIndividualChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalDiaIndividualGuarderia(
            prevModel,
            payload
          );
        });
      },
      onPrecioFinalTransporteIdaChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTransporteIdaGuarderia(
            prevModel,
            payload
          );
        });
      },
      onPrecioFinalTransporteVueltaChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTransporteVueltaGuarderia(
            prevModel,
            payload
          );
        });
      },
      onPrecioFinalTransporteIdaVueltaChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTransporteIdaVueltaGuarderia(
            prevModel,
            payload
          );
        });
      },
    },
    hotel: {
      onPrecioFinalTarifaChange: (payload: ItemCarritoHandlerPayload) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTarifaHotel(prevModel, payload);
        });
      },
      onPrecioFinalNocheIndividualChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalNocheIndividualHotel(prevModel, payload);
        });
      },
      onPrecioFinalTransporteIdaChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTransporteIdaHotel(prevModel, payload);
        });
      },
      onPrecioFinalTransporteVueltaChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTransporteVueltaHotel(prevModel, payload);
        });
      },
      onPrecioFinalTransporteIdaVueltaChange: (
        payload: ItemCarritoHandlerPayload
      ) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTransporteIdaVueltaHotel(
            prevModel,
            payload
          );
        });
      },
    },
    spa: {
      onPrecioFinalTarifaChange: (payload: ItemCarritoHandlerPayload) => {
        setNewModel((prevModel) => {
          return reemplazarPrecioFinalTarifaSpa(prevModel, payload);
        });
      },
    },
    descuento: (val: number) => {
      setNewModel((prevModel) => {
        return recalcularModelConTotal({ ...prevModel, descuento: val });
      });
    },
    eliminarItem: (payload: EliminarItemCarritoPayload) => {
      setNewModel((prevModel) => {
        return eliminarItemHandler(payload, prevModel);
      });
    },
  };

  const onDescuentoChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    const parsedPrecio = parseCurrency(val.target.value, "$", ",");
    handlers.descuento(parsedPrecio);
  };

  const onGuardarClick = () => {
    if (Object.keys(model.itemsPorIdMascota).length === 0) {
      onVaciarCarritoClick();
      return;
    }
    const reemplazoReq = buildNuevoCarritoRequest(model);
    mutarReemplazarCarrito(reemplazoReq);
  };

  const onFinalizarClick = () => {
    mutarAgendar();
  };

  const onVaciarCarritoClick = () => {
    const carritoVacioReq = buildNuevoCarritoVacioRequest();
    mutarReemplazarCarrito(carritoVacioReq);
  };

  if (termino && idVentaResultante) {
    return (
      <Row className="justify-content-center">
        <div className="col-xs-6 col-md-6 col-lg-6 col-xxl-6">
          <ExitoCard
            title={"¡Agendamiento exitoso!"}
            options={generarOpcionesExito(idVentaResultante)}
          />
        </div>
      </Row>
    );
  }

  if (carritoEstaVacio) {
    return <InfoCarritoVacio />;
  }

  return (
    <>
      {isError && <ServerErrorModal />}
      <ContentWrapperCard>
        <Row className="justify-content-center">
          <Col className="col-11">
            <HeaderSection className="pt-5 mb-4">
              <h3
                style={{
                  fontWeight: 500,
                }}
              >
                <IconContainer>
                  <FactCheckIcon sx={{ fontSize: 28 }} />
                </IconContainer>
                Resumen de Reservas
              </h3>
            </HeaderSection>
            <section className="mb-4">
              <h4 className="mb-2" style={{ fontWeight: 500 }}>
                Propietario
              </h4>
              <InfoRow
                label="Nombres"
                value={model.propietario!!.nombreCompleto}
              />
              <InfoRow
                label="Documento"
                value={model.propietario!!.documento}
              />
            </section>

            <SeccionMascotas
              itemsPorIdMascota={model.itemsPorIdMascota}
              handlers={handlers}
            />

            <SeccionTotales
              total={model.total}
              descuento={model.descuento}
              onDescuentoChange={onDescuentoChange}
            />

            <SeccionBotones
              estaSucio={model.estaSucio}
              isLoading={isLoading}
              onGuardarClick={onGuardarClick}
              reemplazarCarritoIsLoading={reemplazarCarritoIsLoading}
              agendarIsLoading={agendarIsLoading}
              onVaciarCarritoClick={onVaciarCarritoClick}
              onFinalizarClick={onFinalizarClick}
            />
          </Col>
        </Row>
      </ContentWrapperCard>
    </>
  );
}

const SeccionBotones = ({
  estaSucio,
  isLoading,
  agendarIsLoading,
  reemplazarCarritoIsLoading,
  onGuardarClick,
  onVaciarCarritoClick,
  onFinalizarClick,
}: {
  estaSucio: boolean;
  isLoading: boolean;
  agendarIsLoading: boolean;
  reemplazarCarritoIsLoading: boolean;
  onGuardarClick: () => void;
  onVaciarCarritoClick: () => void;
  onFinalizarClick: () => void;
}) => {
  return (
    <Row className="d-flex justify-content-between mt-2 mb-2">
      <Col className="d-flex justify-content-end gap-2">
        <Button
          variant="danger"
          onClick={onVaciarCarritoClick}
          size="sm"
          disabled={isLoading}
        >
          <div className="d-flex gap-1">
            {!reemplazarCarritoIsLoading && (
              <DeleteIcon sx={{ fontSize: 18 }} />
            )}
            {reemplazarCarritoIsLoading && (
              <Spinner animation="border" size="sm" />
            )}
            <div>Vaciar</div>
          </div>
        </Button>
        {estaSucio ? (
          <Button
            variant="primary"
            onClick={onGuardarClick}
            size="sm"
            disabled={isLoading}
          >
            <div className="d-flex gap-1">
              {!reemplazarCarritoIsLoading && (
                <SaveIcon sx={{ fontSize: 18 }} />
              )}
              {reemplazarCarritoIsLoading && (
                <Spinner animation="border" size="sm" />
              )}
              <div>Guardar</div>
            </div>
          </Button>
        ) : (
          <SuccessButton
            type={"button"}
            onClick={onFinalizarClick}
            variant="success"
            size="sm"
            disabled={isLoading}
          >
            <div className="d-flex gap-1">
              {!agendarIsLoading && <CheckIcon sx={{ fontSize: 18 }} />}
              {agendarIsLoading && <Spinner animation="border" size="sm" />}
              <div>Finalizar</div>
            </div>
          </SuccessButton>
        )}
      </Col>
    </Row>
  );
};

const InfoCarritoVacio = () => {
  return (
    <div className="d-flex justify-content-center">
      <Alert variant="info" className="w-50">
        <Alert.Heading>
          <span>
            <ShoppingCartIcon />
          </span>{" "}
          Carrito vacío
        </Alert.Heading>
        No se han agregado reservas al carrito, puede añadir agendamientos en{" "}
        <Alert.Link href="/reservas/guarderia/crear">Reservar Guardería</Alert.Link>,{" "}
        <Alert.Link href="/reservas/hotel">Reservar Hotel</Alert.Link> o{" "}
        <Alert.Link href="/reservas/spa">Reservar Spa</Alert.Link>.
      </Alert>
    </div>
  );
};
const SeccionMascotas = ({
  itemsPorIdMascota,
  handlers,
}: {
  itemsPorIdMascota: Record<string, Array<ItemCarrito>>;
  handlers: CarritoHandlers;
}) => {
  return (
    <Accordion defaultActiveKey={"0"} className="mb-4">
      {Object.entries(itemsPorIdMascota).map((entry, index) => {
        const items = entry[1];
        const mascota = items[0].mascota;
        return (
          <Accordion.Item eventKey={index.toString()} key={mascota.id}>
            <Accordion.Header>
              <h4 className="mb-3" style={{ fontWeight: 500 }}>
                <IconContainer>
                  <PetsIcon />
                </IconContainer>
                {mascota.nombre}
              </h4>
            </Accordion.Header>
            <Accordion.Body>
              <SeccionMascota
                handlers={handlers}
                items={items}
                mascota={mascota}
              />
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};

const SeccionTotales = ({
  descuento,
  total,
  onDescuentoChange,
}: {
  total: number;
  descuento: number;
  onDescuentoChange: (val: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <section className="mb-4">
      <h4 className="mb-2" style={{ fontWeight: 500 }}>
        Totales
      </h4>
      <Row className="mb-2">
        <Col className="col-4" style={{ fontWeight: 500 }}>
          Descuento
        </Col>
        <Col></Col>
        <Col className="justify-content-right">
          <CurrencyFormat
            className="w-100"
            name="descuento"
            value={descuento}
            onChange={onDescuentoChange}
            thousandSeparator={","}
            prefix={"$"}
            decimalSeparator={"."}
            min={0}
            max={total}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="col-4" style={{ fontWeight: 500 }}>
          Total
        </Col>
        <Col></Col>
        <Col className="justify-content-right">
          <CurrencyFormat
            className="w-100"
            name="total"
            value={total}
            thousandSeparator={","}
            disabled={true}
            prefix={"$"}
            decimalSeparator={"."}
          />
        </Col>
      </Row>
    </section>
  );
};

export const HeaderSection = styled(Row)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`;

const generarOpcionesExito = (idVenta: string): ExitoOption[] => {
  return [
    {
      description: "Ver Venta",
      path: "/ventas/" + idVenta,
      variant: "primary",
    },
    {
      description: "Realizar nueva Reserva",
      path: "/reservas",
      variant: "secondary",
      isLast: true,
    },
  ];
};

export default Resumen;
